import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/lib/core/providers/appProvider';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { ScreenHead } from '@/components/shared/screen/head';
import { Icon } from '@/components/ui/icon';
import { SpinLoading } from '@/components/shared/loadings/spin';
import type {
  EventListItemDto,
  EventsCalendarPropertiesModel,
  IApiContentResponseModel,
} from '@/services/platform/api/model';
import type { AppContext } from '@/lib/core/types';
import {
  EMPTY_EVENT_FILTER,
  useEventCategories,
  useEventDates,
  useEventsByDay,
  useEventFavoriteIndex,
  useEventVenues,
  visibilityParam,
  type EventFilterState,
} from '@/services/platform/data/events';
import { HubDropdown } from './HubDropdown';
import { EventCategoryPills, type EventCategoryPillItem } from './EventCategoryPills';
import { CalendarRail } from './CalendarRail';
import { EventCard } from './EventCard';
import { DateDivider } from './DateDivider';
import { SoldOutEventSheet } from './SoldOutEventSheet';
import { RequestPrivateEventSheet } from './RequestPrivateEventSheet';
import { EventsFilterSheet } from './EventsFilterSheet';
import { EventsSearchOverlay } from '../search';
import { applyCategoriesByDay, categoryCounts } from './facets';
import { resolveDateJump } from './dateJump';
import { UNCATEGORISED_EVENT_CATEGORY } from './categories';
import { formatDayGroupLabel } from './format';
import { isMembersOnly } from './visibility';
import { isCardSoldOut } from '../rules';

export interface EventsCalendarScreenProps {
  content: IApiContentResponseModel;
  context?: AppContext;
}

/**
 * Events List Page (ELP) — core screen. Fed by the CMS `eventsCalendar` node
 * (page name + `hubPicker`) and the `/api/v1/events*` endpoints. Shows a hub
 * dropdown, calendar-category pills, a left calendar date-rail and a
 * scrollable list of event cards grouped by day. Campaigns (recurring events) are not a section of
 * their own: each sits under every day its recurrence runs, mixed with the dated events by hour.
 *
 * The rail's date tap is a JUMP, not a filter: the list always holds everything upcoming, and
 * tapping a day scrolls to its block. Nothing about the marked day reaches a request. What does
 * filter: the hub dropdown, the category pills (client-side — `/events/categories` is not
 * date-aware) and the filter overlay's venues, date presets and "Members only".
 */
export function EventsCalendarScreen({ content }: EventsCalendarScreenProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { user, appSettings } = useApp();
  const setMainClassName = useUIStore(state => state.setMainClassName);

  const isMember = !!user?.hasActiveMembership;
  const eventDetailPath = appSettings?.properties?.eventDetail?.route?.path;

  const cms = content?.properties as EventsCalendarPropertiesModel | undefined;
  const hubs = useMemo(() => cms?.hubPicker ?? [], [cms]);
  const title = cms?.pageName || content?.name || t('App.Events.Title');

  const [filter, setFilter] = useState<EventFilterState>(EMPTY_EVENT_FILTER);

  /** The rail's marked day — a scroll position, never part of a request. */
  const [markedDate, setMarkedDate] = useState<string | undefined>(undefined);

  // "Members only" narrows the list, the rail and the pill counts alike — one param, three requests.
  const visibility = visibilityParam(filter.membersOnly);
  const { categories: categoryFacets } = useEventCategories(filter.hubKey, visibility);
  const { venues } = useEventVenues(filter.hubKey);
  // Unfiltered by category — the rail's full range and "has any content at all" set (contentKeys
  // in CalendarRail). Kept separate from the category-scoped call below so the rail's range
  // doesn't shrink/jump every time a pill is toggled.
  // Campaigns are counted in (no `isCampaign`): the server lights the days their recurrence
  // actually runs on — the same days the list's cards carry in `days[]`.
  const { dates } = useEventDates({
    hubKey: filter.hubKey,
    venueId: filter.venueIds,
    datePreset: filter.datePresets,
    visibility,
    includeRestricted: true,
  });
  // Category-scoped — which of those days also match the active category selection. The
  // "uncategorised" sentinel isn't a real server value, so it's excluded from the request;
  // with only it selected this falls back to the unfiltered set (over-lighting, never under-).
  const serverCategories = filter.categories.filter(c => c !== UNCATEGORISED_EVENT_CATEGORY);
  const { dates: categoryDates } = useEventDates(
    {
      hubKey: filter.hubKey,
      venueId: filter.venueIds,
      datePreset: filter.datePresets,
      visibility,
      includeRestricted: true,
      eventCategory: serverCategories,
    },
    serverCategories.length > 0
  );
  // includeRestricted: true — the ELP shows every card, including Members Only/Invite
  // Only ones, dimmed for non-members (`disabled={!isMember}` below); it's a display
  // list, not a choice, so locked cards are still shown rather than hidden.
  // No `isCampaign` and no `date`: one request holds dated events and campaigns alike, and the
  // rail marks a day rather than narrowing the list. The overlay's date presets still apply.
  const {
    days,
    events,
    isLoading: isLoadingEvents,
    hasFetched: hasFetchedEvents,
  } = useEventsByDay({
    hubKey: filter.hubKey,
    venueId: filter.venueIds,
    datePreset: filter.datePresets,
    visibility,
    includeRestricted: true,
  });
  const isLoading = isLoadingEvents && !hasFetchedEvents;

  // The screen owns a `fixed inset-0` viewport box (below) so the rail and list
  // scroll independently — no dependence on `main` being height-bounded. Just
  // clear main's default bottom padding while mounted.
  useEffect(() => {
    setMainClassName('pb-0!');
    return () => setMainClassName('');
  }, [setMainClassName]);

  const {
    eventFavoriteLists,
    favoriteListGuidByEventId,
    refetch: refetchEventFavorites,
  } = useEventFavoriteIndex();

  // Sold-out (capacity, orthogonal to visibility) takes priority. A non-member
  // tapping a members-only card is a no-op — non-members cannot tap, open, or
  // explore members-only event/campaign details; everything else lands on the
  // real detail screen.
  const [soldOutEvent, setSoldOutEvent] = useState<EventListItemDto | undefined>(undefined);
  const handleSelectEvent = useCallback(
    (event: EventListItemDto, dayKey?: string) => {
      if (!isMember && isMembersOnly(event.visibility)) {
        return;
      }
      if (isCardSoldOut(event)) {
        setSoldOutEvent(event);
        return;
      }
      if (event.id) navigate(`${eventDetailPath}/${event.id}`, { state: { selectedDate: dayKey } });
    },
    [isMember, navigate, eventDetailPath]
  );

  const handleToggleCategory = useCallback((value: string) => {
    setFilter(prev => {
      const categories = prev.categories.includes(value)
        ? prev.categories.filter(c => c !== value)
        : [...prev.categories, value];
      return { ...prev, categories };
    });
  }, []);

  const [isRequestPrivateEventOpen, setRequestPrivateEventOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isFilterOverlayOpen, setFilterOverlayOpen] = useState(false);

  /** Rendered day blocks, by day key — the rail's jump targets. */
  const dayRefs = useRef(new Map<string, HTMLElement>());
  const registerDay = useCallback((key: string, node: HTMLElement | null) => {
    if (node) dayRefs.current.set(key, node);
    else dayRefs.current.delete(key);
  }, []);

  /**
   * A rail tap marks the day and scrolls to it. The scroll happens here rather than in an effect
   * on `markedDate` so that tapping the day already marked jumps again instead of doing nothing.
   */
  const handleSelectDate = useCallback((dateKey: string) => {
    setMarkedDate(dateKey);
    const target = resolveDateJump(dateKey, [...dayRefs.current.keys()]);
    if (target) dayRefs.current.get(target.key)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // The rail lights a day when it has content (`dates`, unfiltered) AND matches the active
  // category selection — `categoryDates` when a real category is selected, else `dates` itself
  // (no narrowing to apply). Day membership is now entirely server-computed (`useEventsByDay`).
  const enabledDateKeys = useMemo(
    () =>
      (serverCategories.length > 0 ? categoryDates : dates).map(d => d.date).filter((d): d is string => !!d),
    [serverCategories.length, categoryDates, dates]
  );
  const categoryItems = useMemo(() => {
    // Counts come from the server facet, which is exact beyond the one page we hold, already
    // counts campaigns and follows "Members only". There is nothing left to scope them to: the rail
    // marks a day rather than narrowing the list, so every upcoming event is on screen whatever
    // day is marked.
    const items: EventCategoryPillItem[] = [];
    for (const facet of categoryFacets) {
      if (!facet.value) continue;
      const count = facet.count ?? 0;
      items.push({ value: facet.value, count, disabled: count === 0 });
    }
    // The server's category facet only counts non-null `eventCategory` values (contract §8:
    // the picklist has legacy gaps) — synthesize an "Other" pill so an uncategorised event
    // stays reachable instead of vanishing as soon as any pill is selected. Counted over what we
    // hold (each event once, however many days it is listed under), since the server never
    // reports this bucket.
    const uncategorisedCount = categoryCounts(events).get(UNCATEGORISED_EVENT_CATEGORY) ?? 0;
    if (uncategorisedCount > 0 || filter.categories.includes(UNCATEGORISED_EVENT_CATEGORY)) {
      items.push({
        value: UNCATEGORISED_EVENT_CATEGORY,
        count: uncategorisedCount,
        disabled: uncategorisedCount === 0,
      });
    }
    return items;
  }, [categoryFacets, events, filter.categories]);
  const visibleDays = useMemo(() => applyCategoriesByDay(days, filter.categories), [days, filter.categories]);

  // The marked day is deliberately absent: it is a scroll position, so it must not raise
  // "Clear all" or turn the empty state into the filtered one.
  const hasActiveFilters =
    !!filter.hubKey ||
    filter.categories.length > 0 ||
    filter.venueIds.length > 0 ||
    filter.datePresets.length > 0 ||
    filter.membersOnly;

  // No events to show either way; which empty state to render depends on
  // whether that's because the hub genuinely has nothing upcoming, or
  // because the active filters narrowed it down to zero.
  const isNoResults = !isLoading && visibleDays.length === 0;
  const isEmpty = isNoResults && !hasActiveFilters;
  const isFilteredEmpty = isNoResults && hasActiveFilters;

  const searchAction = isMember ? (
    <button
      type="button"
      aria-label={t('App.Labels.Search')}
      onClick={() => setSearchOpen(true)}
      className="flex size-11 items-center justify-center"
    >
      <Icon name="search" width={20} height={20} strokeWidth={1.5} className="text-jncquoi-neutral-1000" />
    </button>
  ) : undefined;

  return (
    <div className="pt-safe-area fixed inset-0 z-30 flex flex-col bg-white">
      <div className="shrink-0 bg-white">
        <ScreenHead
          title={title}
          onBack={() => navigate(-1)}
          ariaLabel={t('App.Labels.GoBack')}
          rightActions={searchAction}
          className="pt-0"
        />
        <div className="flex items-center px-4">
          <HubDropdown
            hubs={hubs}
            value={filter.hubKey}
            onChange={hubKey => setFilter(prev => ({ ...prev, hubKey }))}
          />
          {isMember && (
            <button
              type="button"
              aria-label={t('App.Events.Filters')}
              onClick={() => setFilterOverlayOpen(true)}
              className="ml-auto flex size-11 items-center justify-center"
            >
              <Icon name="filter-list" width={24} height={24} className="text-jncquoi-neutral-1000" />
            </button>
          )}
        </div>
        <div className="border-jncquoi-neutral-200 border-b">
          <EventCategoryPills
            items={categoryItems}
            activeValues={filter.categories}
            onToggle={handleToggleCategory}
            onAddEvent={() => setRequestPrivateEventOpen(true)}
            showClearAll={hasActiveFilters}
            onClearAll={() =>
              setFilter(prev => ({
                ...prev,
                hubKey: undefined,
                categories: [],
                venueIds: [],
                datePresets: [],
                membersOnly: false,
              }))
            }
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 pl-4">
        <CalendarRail
          dates={dates}
          enabledDateKeys={enabledDateKeys}
          markedDate={markedDate}
          onSelectDate={handleSelectDate}
        />
        <div className="min-h-0 min-w-0 flex-1 scrollbar-none overflow-y-auto pt-4 pr-4 pb-24">
          {isLoading ? (
            <SpinLoading wrapperClassName="min-h-[40vh]" />
          ) : isEmpty ? (
            <div className="flex flex-col items-center gap-2 px-4 pt-16 text-center">
              <Icon name="calendar" width={24} height={24} className="text-jncquoi-neutral-1000" />
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
                {t('App.Events.EmptyTitle')}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[14px] tracking-[0.5px]">
                {t('App.Events.EmptySubtitle')}
              </p>
            </div>
          ) : isFilteredEmpty ? (
            <div className="flex flex-col items-center gap-2 px-4 pt-16 text-center">
              <div className="flex size-11 items-center justify-center">
                <Icon name="area-search" width={20} height={20} className="text-jncquoi-neutral-1000" />
              </div>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
                {t('App.Events.NoResultsTitle')}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[14px] tracking-[0.5px]">
                {t('App.Events.NoResultsSubtitle')}
              </p>
            </div>
          ) : (
            <>
              {visibleDays.map(([key, items]) => (
                // The ref callback keeps a block body on purpose: React 19 reads a returned value
                // from a ref callback as a cleanup function, so it must not implicitly return one.
                <section
                  key={key}
                  ref={node => {
                    registerDay(key, node);
                  }}
                  className="pb-3"
                >
                  <DateDivider label={formatDayGroupLabel(key, locale)} />
                  <div className="flex flex-col gap-4 pt-6">
                    {items.map(event => (
                      <EventCard
                        // Keyed by its slot too: the same event can be listed twice under one day.
                        key={`${event.id}-${event.startDateTime}`}
                        event={event}
                        disabled={!isMember && isMembersOnly(event.visibility)}
                        favoriteLists={eventFavoriteLists}
                        refetchFavoriteLists={refetchEventFavorites}
                        favoriteListGuid={event.id ? favoriteListGuidByEventId.get(event.id) : undefined}
                        onRefetchFavoritesNeeded={refetchEventFavorites}
                        onSelect={event => handleSelectEvent(event, key)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </>
          )}
        </div>
      </div>

      <SoldOutEventSheet
        open={!!soldOutEvent}
        onOpenChange={open => !open && setSoldOutEvent(undefined)}
        onViewAnyway={() => {
          if (soldOutEvent?.id) navigate(`${eventDetailPath}/${soldOutEvent.id}`);
        }}
      />
      <RequestPrivateEventSheet open={isRequestPrivateEventOpen} onOpenChange={setRequestPrivateEventOpen} />
      <EventsSearchOverlay open={isSearchOpen} onClose={() => setSearchOpen(false)} hubKey={filter.hubKey} />
      <EventsFilterSheet
        open={isFilterOverlayOpen}
        onOpenChange={setFilterOverlayOpen}
        hubKey={filter.hubKey}
        venues={venues}
        selectedVenueIds={filter.venueIds}
        selectedDatePresets={filter.datePresets}
        selectedMembersOnly={filter.membersOnly}
        onApply={selection => setFilter(prev => ({ ...prev, ...selection }))}
        onClearAll={() => setFilter(prev => ({ ...prev, venueIds: [], datePresets: [], membersOnly: false }))}
      />
    </div>
  );
}
